/**
 * The well-job writes (api#1055): create / edit, the forward step, cancel,
 * a stamp correction, and putting an assembly on the job.
 *
 * EVERY REFUSAL IS SHOWN AS THE API WROTE IT. The API answers `{ code, error }`
 * (DD_MODE_OFF, JOB_TRANSITION_NOT_ALLOWED, JOB_CLOSED, RUN_STILL_OPEN,
 * STAMP_OUT_OF_ORDER, ASSEMBLY_ON_OTHER_JOB, …) and its sentences already name
 * the job, the run or the stamp. Nothing here rewrites them.
 *
 * TIMES ARE THE TENANT'S WALL CLOCK, as on the run-log dialogs. A
 * datetime-local value has no offset and the API reads an offset-free value in
 * the company's zone, so the picker starts from the instant in that zone and
 * the value is sent as typed. A blank step time is left out: the API stamps now.
 *
 * AN EDIT SENDS ONLY WHAT CHANGED. A closed job takes notes only (JOB_CLOSED),
 * so the other fields are disabled there and never sent.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import ProvinceSelect from '../../components/ProvinceSelect';
import { useLocale } from '../../contexts/LocaleContext';
import { useCreateJob, useJobTransition, useUpdateJob, useAttachJobAssembly } from '../../hooks/useDownholeJobs';
import { useDownholeAssemblies } from '../../hooks/useDownhole';
import { unwrapArray } from '../../lib/api-envelope';
import { DH_JOB_STAMP_LABEL, isJobClosed, type DhJobStep } from '../../lib/downhole-jobs';
import { onlineOrSay } from '../../lib/downhole-offline';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../lib/load-failure';
import type { DhAssembly, DhJob, DhJobDetail, DhJobStampColumn, DhJobWrite } from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhField, useOpenCount } from './DownholeBits';
import { tenantLocalInput } from './RunDialogs';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-3 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

export function useCustomers(enabled: boolean) {
  return useQuery({
    queryKey: ['customers'],
    queryFn: async () => unwrapArray<{ id: string; name: string }>((await api.customers.list()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── Create / edit ───────────────────────────────────────────────────────────

const TEXT_KEYS = ['well_name', 'uwi', 'api_number', 'lsd', 'well_licence', 'afe_number', 'po_number', 'rig_name', 'notes'] as const;
type TextKey = (typeof TEXT_KEYS)[number];
type FormValues = Record<TextKey | 'customer_id' | 'province_state' | 'scheduled_start', string>;

/** Max lengths, the API's (api#1059). */
const MAX: Record<TextKey, number> = {
  well_name: 255, uwi: 50, api_number: 20, lsd: 100, well_licence: 50, afe_number: 100, po_number: 100, rig_name: 255, notes: 10000,
};

function initialValues(job: DhJob | null): FormValues {
  return {
    customer_id: job?.customer_id ?? '',
    province_state: job?.province_state ?? '',
    scheduled_start: job?.scheduled_start ?? '',
    well_name: job?.well_name ?? '',
    uwi: job?.uwi ?? '',
    api_number: job?.api_number ?? '',
    lsd: job?.lsd ?? '',
    well_licence: job?.well_licence ?? '',
    afe_number: job?.afe_number ?? '',
    po_number: job?.po_number ?? '',
    rig_name: job?.rig_name ?? '',
    notes: job?.notes ?? '',
  };
}

/**
 * The body to send. A create sends every filled field; an edit sends only the
 * fields that moved, with a cleared optional field as null.
 */
export function jobWriteBody(values: FormValues, job: DhJob | null): DhJobWrite {
  const before = initialValues(job);
  const body: Record<string, string | null> = {};
  (Object.keys(values) as (keyof FormValues)[]).forEach((k) => {
    const v = values[k].trim();
    if (job) {
      if (v === before[k].trim()) return;
      body[k] = v === '' ? null : v;
    } else if (v !== '') {
      body[k] = v;
    }
  });
  return body as DhJobWrite;
}

export function JobFormDialog({ open, job, onClose, onSaved }: {
  open: boolean;
  /** null creates. */
  job: DhJob | null;
  onClose: () => void;
  onSaved: (job: DhJobDetail) => void;
}) {
  const { locale } = useLocale();
  const country = locale.country === 'US' ? 'US' : 'CA';
  const create = useCreateJob();
  const update = useUpdateJob();
  const customersQuery = useCustomers(open);
  const openCount = useOpenCount(open);
  const [values, setValues] = useState<FormValues>(() => initialValues(job));
  // api#1062. Absent on a job read from before batch 4: unknown, starts off and is sent only if ticked.
  const [footage, setFootage] = useState<boolean>(job?.footage_billing === true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    setValues(initialValues(job));
    setFootage(job?.footage_billing === true);
    setError(null);
    // Reset when the dialog opens, never on a refetch of the same job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const closed = !!job && isJobClosed(job.status);
  const pending = create.isPending || update.isPending;
  const set = (k: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setValues((v) => ({ ...v, [k]: e.target.value }));
  const customers = customersQuery.data ?? [];
  // The job's own customer stays selectable even if the list could not load.
  const customerOptions = job?.customer_id && !customers.some((c) => c.id === job.customer_id)
    ? [{ id: job.customer_id, name: job.customer_name ?? 'Current customer' }, ...customers]
    : customers;

  const submit = () => {
    setError(null);
    if (!values.customer_id) return setError('Choose the customer.');
    if (!values.well_name.trim()) return setError('Enter the well name.');
    const body = jobWriteBody(values, job);
    // Sent only when it moved. Changing it sends every day's ticket back to draft.
    if (job ? footage !== (job.footage_billing === true) : footage) body.footage_billing = footage;
    if (job && Object.keys(body).length === 0) return onClose();
    if (!onlineOrSay(setError)) return;
    const done = {
      onSuccess: (res: { data: DhJobDetail | null }) => {
        toast.success(job ? 'Well job saved' : 'Well job created');
        if (res.data) onSaved(res.data);
        onClose();
      },
      onError: (err: unknown) => setError(getApiErrorMessage(err, 'The well job could not be saved.')),
    };
    if (job) update.mutate({ id: job.id, body }, done);
    else create.mutate(body, done);
  };

  // CA wells are named by UWI, LSD and licence; US wells by API number. The
  // API takes both, so a value already on the job in the other scheme is shown.
  const showCa = country === 'CA' || !!(job?.uwi || job?.lsd || job?.well_licence);
  const showUs = country === 'US' || !!job?.api_number;
  const text = (k: TextKey, label: string, opts: { required?: boolean } = {}) => (
    <DhField label={opts.required ? `${label} *` : label}>
      <input className={DH_INPUT} value={values[k]} maxLength={MAX[k]} onChange={set(k)} disabled={closed || pending} aria-label={label} />
    </DhField>
  );

  return (
    <DhDialog
      open={open}
      title={job ? `Edit ${job.job_number}` : 'New well job'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={pending}>Cancel</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={pending}>
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {job ? 'Save' : 'Create well job'}
          </button>
        </>
      }
    >
      {closed && (
        <p className="mb-3 text-sm text-fieldiq-muted">This job is closed. Only its notes can be changed.</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <DhField label="Customer *">
          <select
            className={DH_INPUT}
            value={values.customer_id}
            onChange={set('customer_id')}
            disabled={closed || pending}
            aria-label="Customer"
          >
            <option value="">Choose a customer</option>
            {customerOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {queryLoadFailure(customersQuery) && (
            <span className="mt-1 block text-xs text-fieldiq-muted">Customers could not be loaded.</span>
          )}
        </DhField>
        {text('well_name', 'Well name', { required: true })}
        {showCa && text('uwi', 'UWI')}
        {showCa && text('lsd', 'LSD')}
        {showCa && text('well_licence', 'Well licence')}
        {showUs && text('api_number', 'API number')}
        {text('rig_name', 'Rig')}
        <DhField label={country === 'US' ? 'State' : 'Province'}>
          <ProvinceSelect
            value={values.province_state}
            onChange={(v) => setValues((s) => ({ ...s, province_state: v }))}
            country={country}
            className={DH_INPUT}
            disabled={closed || pending}
            aria-label={country === 'US' ? 'State' : 'Province'}
          />
        </DhField>
        <DhField label="Scheduled start">
          <input type="date" className={DH_DATE_INPUT} value={values.scheduled_start} onChange={set('scheduled_start')} disabled={closed || pending} aria-label="Scheduled start" />
        </DhField>
        {text('afe_number', 'AFE')}
        {text('po_number', 'PO')}
      </div>
      <label className="mt-3 flex items-start gap-2 text-sm text-fieldiq-text">
        <input
          type="checkbox"
          className="mt-1"
          checked={footage}
          onChange={(e) => setFootage(e.target.checked)}
          disabled={closed || pending}
          aria-label="Bill footage per day"
        />
        <span>
          Bill footage per day
          <span className="block text-xs text-fieldiq-muted">Each day then needs its start and end depths. Changing this re-prices every day of the job.</span>
        </span>
      </label>
      <div className="mt-3">
        <DhField label="Notes">
          <textarea
            className={`${DH_INPUT} h-24 py-2`}
            value={values.notes}
            maxLength={MAX.notes}
            onChange={set('notes')}
            disabled={pending}
            aria-label="Notes"
          />
        </DhField>
      </div>
      <ErrorLine message={error} />
    </DhDialog>
  );
}

// ─── Forward step ────────────────────────────────────────────────────────────

export function StepDialog({ open, job, step, onClose }: {
  open: boolean;
  job: DhJob;
  step: DhJobStep;
  onClose: () => void;
}) {
  const transition = useJobTransition();
  const openCount = useOpenCount(open);
  const [at, setAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    setAt('');
    setError(null);
  }, [openCount]);

  const submit = () => {
    setError(null);
    if (!onlineOrSay(setError)) return;
    transition.mutate(
      { id: job.id, body: at ? { status: step.to, at } : { status: step.to } },
      {
        onSuccess: () => {
          toast.success(`${job.job_number}: ${step.label}`);
          onClose();
        },
        onError: (err) => setError(getApiErrorMessage(err, 'The job could not be moved.')),
      },
    );
  };

  return (
    <DhDialog
      open={open}
      title={`${step.label}: ${job.job_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={transition.isPending}>Cancel</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={transition.isPending}>
            {transition.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {step.label}
          </button>
        </>
      }
    >
      <DhField label={`${DH_JOB_STAMP_LABEL[step.stamp]} at`} hint="Leave blank for now. Times are on the company's clock.">
        <input type="datetime-local" className={DH_DATE_INPUT} value={at} onChange={(e) => setAt(e.target.value)} aria-label={`${DH_JOB_STAMP_LABEL[step.stamp]} at`} />
      </DhField>
      <ErrorLine message={error} />
    </DhDialog>
  );
}

// ─── Cancel ──────────────────────────────────────────────────────────────────

export function CancelJobDialog({ open, job, onClose }: { open: boolean; job: DhJob; onClose: () => void }) {
  const transition = useJobTransition();
  const openCount = useOpenCount(open);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    setReason('');
    setError(null);
  }, [openCount]);

  const submit = () => {
    setError(null);
    if (!reason.trim()) return setError('Say why the job is being cancelled.');
    if (!onlineOrSay(setError)) return;
    transition.mutate(
      { id: job.id, body: { status: 'cancelled', cancel_reason: reason.trim() } },
      {
        onSuccess: () => {
          toast.success(`${job.job_number} cancelled`);
          onClose();
        },
        onError: (err) => setError(getApiErrorMessage(err, 'The job could not be cancelled.')),
      },
    );
  };

  return (
    <DhDialog
      open={open}
      title={`Cancel ${job.job_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={transition.isPending}>Keep the job</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={transition.isPending}>
            {transition.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Cancel job
          </button>
        </>
      }
    >
      <DhField label="Reason *">
        <textarea className={`${DH_INPUT} h-24 py-2`} value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} aria-label="Reason" />
      </DhField>
      <ErrorLine message={error} />
    </DhDialog>
  );
}

// ─── Stamp correction ────────────────────────────────────────────────────────

export function StampDialog({ open, job, stamp, timeZone, onClose }: {
  open: boolean;
  job: DhJob;
  stamp: DhJobStampColumn;
  timeZone: string | null;
  onClose: () => void;
}) {
  const update = useUpdateJob();
  const openCount = useOpenCount(open);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    setValue(tenantLocalInput(job[stamp], timeZone));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const submit = () => {
    setError(null);
    if (!value) return setError(`Enter the ${DH_JOB_STAMP_LABEL[stamp].toLowerCase()} time.`);
    if (value === tenantLocalInput(job[stamp], timeZone)) return onClose();
    if (!onlineOrSay(setError)) return;
    update.mutate(
      { id: job.id, body: { [stamp]: value } },
      {
        onSuccess: () => {
          toast.success(`${DH_JOB_STAMP_LABEL[stamp]} time corrected`);
          onClose();
        },
        onError: (err) => setError(getApiErrorMessage(err, 'The time could not be corrected.')),
      },
    );
  };

  return (
    <DhDialog
      open={open}
      title={`Correct ${DH_JOB_STAMP_LABEL[stamp].toLowerCase()} time`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={update.isPending}>Cancel</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={update.isPending}>
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </>
      }
    >
      <DhField label={DH_JOB_STAMP_LABEL[stamp]} hint="On the company's clock.">
        <input type="datetime-local" className={DH_DATE_INPUT} value={value} onChange={(e) => setValue(e.target.value)} aria-label={DH_JOB_STAMP_LABEL[stamp]} />
      </DhField>
      <ErrorLine message={error} />
    </DhDialog>
  );
}

// ─── Add assembly ────────────────────────────────────────────────────────────

/**
 * Which assemblies the API could accept. Hidden: broken down, on a well job
 * already (`dh_job_id` set, api#1062; this job's own are in `job.assemblies`
 * too, which covers a list read from before the link arrived), or already
 * naming a different customer. An assembly whose `dh_job_id` is ABSENT (mode off, or an API before
 * batch 4) is unknown, not free, and stays offered; the API then answers
 * ASSEMBLY_ON_OTHER_JOB naming the job, shown as written.
 */
export function attachableAssemblies(assemblies: DhAssembly[], job: Pick<DhJobDetail, 'customer_id' | 'assemblies'>): DhAssembly[] {
  const onJob = new Set(job.assemblies.map((a) => a.id));
  return assemblies.filter(
    (a) =>
      a.status !== 'disassembled' &&
      !onJob.has(a.id) &&
      typeof a.dh_job_id !== 'string' &&
      (!a.customer_id || a.customer_id === job.customer_id),
  );
}

export function AttachAssemblyDialog({ open, job, onClose }: { open: boolean; job: DhJobDetail; onClose: () => void }) {
  const attach = useAttachJobAssembly();
  const listQuery = useDownholeAssemblies(undefined);
  const openCount = useOpenCount(open);
  const [choice, setChoice] = useState('');
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    setChoice('');
    setError(null);
  }, [openCount]);

  const offered = attachableAssemblies(listQuery.data?.data ?? [], job);
  const failure = queryLoadFailure(listQuery);

  const submit = () => {
    setError(null);
    if (!choice) return setError('Choose an assembly.');
    if (!onlineOrSay(setError)) return;
    attach.mutate(
      { id: job.id, assembly_id: choice },
      {
        onSuccess: () => {
          toast.success('Assembly added to the job');
          onClose();
        },
        onError: (err) => setError(getApiErrorMessage(err, 'The assembly could not be added.')),
      },
    );
  };

  return (
    <DhDialog
      open={open}
      title={`Add an assembly to ${job.job_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={attach.isPending}>Cancel</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={attach.isPending || !choice}>
            {attach.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Add assembly
          </button>
        </>
      }
    >
      {listQuery.isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading assemblies" /></div>
      ) : failure ? (
        <p className="text-sm text-fieldiq-text">The assemblies could not be loaded, so none can be added right now.</p>
      ) : offered.length === 0 ? (
        <p className="text-sm text-fieldiq-text">No assembly can be added: every one is broken down, on a well job already, or for another customer.</p>
      ) : (
        <DhField label="Assembly" hint="Its runs come with it, and it takes this job's customer.">
          <select className={DH_INPUT} value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Assembly">
            <option value="">Choose an assembly</option>
            {offered.map((a) => (
              <option key={a.id} value={a.id}>{`${a.assembly_number} · ${a.name}`}</option>
            ))}
          </select>
        </DhField>
      )}
      <ErrorLine message={error} />
    </DhDialog>
  );
}
